import React, { useState, useEffect, useMemo } from 'react'
import CustomTable from '../../components/Table'
import CustomTableHead from '../../components/Table/TableHead'
import CustomTableBody from '../../components/Table/TableBody'
import CustomTableRow from '../../components/Table/TableRow'
import CustomTableData from '../../components/Table/TableData'
import SearchIcon from '../../assets/icons/search.svg'
import profileIcon from '../../assets/icons/profile-icon.svg'
import Modal from '../../components/Modal'
import PropertyService from '../../api/property-service'
import CustomTableDataCTA from '../../components/Table/TableDataCTA'
import TablePagination from '../../components/Table/Pagination'
import { PropertyDTO, MemberDTO } from '../../interfaces/landlord'
import { showToast } from '../../utils/toast'
import { handleErrorResponse } from '../../utils/handleErrorResponse'
import DefaultButton from '../../components/Button/Button'
import { isEstateApproved } from '../../utils/estateApproval'
import { useDebounce } from '../../hooks/useDebounce'

const tableHeadData = ['Landlord', 'Address', 'No. of Tenants', 'status', '']

type ModalType = 'tenant' | 'landlord' | 'capacity' | 'property' | 'approval' | null
interface ViewPropertyProps {
  handleViewSelect: (view: string, queries?: Record<string, string>) => void
}

const ViewProperty: React.FC<ViewPropertyProps> = ({ handleViewSelect }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [properties, setProperties] = useState<PropertyDTO[]>([])
  const [totalPages, setTotalPages] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [selectedProperty, setSelectedProperty] = useState<PropertyDTO | null>(null)
  const [modalType, setModalType] = useState<ModalType>(null)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [rejectLoading, setRejectLoading] = useState(false)
  const [approveLoading, setApproveLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 400)

  const {
    data,
    isLoading: loading,
    mutate,
  } = PropertyService.useGetPropertyList({
    page: currentPage,
    size: 10,
    name: debouncedSearchQuery,
  })

  const propertyId = selectedProperty?.id ?? ''

  const { data: tenantsData, isLoading: tenantsLoading } =
    PropertyService.useGetPropertyTenants(propertyId)

  const landlordProperties = properties.filter(
    (property) =>
      property.communityMemberId?.resident?.id ===
      selectedProperty?.communityMemberId?.resident?.id
  )

  const visibleProperties = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()

    if (!query) return properties

    return properties.filter((property) => {
      const resident = property.communityMemberId?.resident
      const searchableText = [
        resident?.firstName,
        resident?.lastName,
        resident?.fullName,
        property.address,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return searchableText.includes(query)
    })
  }, [properties, searchQuery])

  useEffect(() => {
    if (data) {
      setProperties(data.results || [])
      setTotalPages(data.totalPages || 1)
      setIsLoading(loading)
    }
  }, [data, loading])

  const handleOpenModal = (type: ModalType, property: PropertyDTO) => {
    setSelectedProperty(property)
    setModalType(type)
  }

  const handleCloseModal = () => {
    setSelectedProperty(null)
    setModalType(null)
  }
  const handleOpenRejectModal = () => {
    setModalType(null)
    setShowRejectModal(true)
  }
  const handleApproveProperty = async (property: PropertyDTO) => {
    try {
      setApproveLoading(true)
      const response = await PropertyService.approveProperty(property.id, {
        status: true,
      })

      showToast({
        description: response.message || 'Property approved successfully',
        status: 'success',
      })
      await mutate()
      handleCloseModal()
    } catch (error) {
      handleErrorResponse(error)

      showToast({
        description: 'Failed to approve property',
        status: 'error',
      })
    } finally {
      setApproveLoading(false)
    }
  }

  const handleRejectProperty = async () => {
    if (!selectedProperty || !rejectReason.trim()) return

    try {
      setRejectLoading(true)

      const response = await PropertyService.approveProperty(selectedProperty.id, {
        status: false,
        reason: rejectReason.trim(),
      })

      showToast({
        description: response.message || 'Property rejected successfully',
        status: 'success',
      })
      await mutate()

      setShowRejectModal(false)
      setRejectReason('')
      handleCloseModal()
    } catch (error) {
      console.error('Failed to reject property:', error)

      showToast({
        description: 'Failed to reject property',
        status: 'error',
      })
    } finally {
      setRejectLoading(false)
    }
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
    setCurrentPage(1)
  }

  return (
    <div className="mt-6 md:mt-12 grid gap-y-6 md:gap-y-10 px-1 md:px-0">
      {/* Search Bar */}
      <form
        onSubmit={(e) => {
          e.preventDefault()
        }}
        className="search-filter-bar flex w-full justify-between gap-2.5"
      >
        <div className="w-full flex bg-gray-100 rounded-lg items-center px-3">
          <div className="relative flex items-center w-full">
            <img src={SearchIcon} alt="Search Icon" className="mr-2 w-5 h-5" />
            <input
              placeholder="Search"
              className="py-3 md:py-4 w-full bg-transparent outline-none text-sm md:text-base"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </form>

      {/* Main Table Container: Adds horizontal scroll on small viewports */}
      <div className="w-full overflow-x-auto rounded-lg shadow-sm max-w-full block whitespace-nowrap min-w-full">
        <CustomTable>
          <CustomTableHead dataList={tableHeadData} />
          <CustomTableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <CustomTableRow key={index}>
                  <CustomTableData dataLabel="Landlord">
                    <div className="flex items-center space-x-3 animate-pulse">
                      <div className="w-10 h-10 bg-gray-300 rounded-full shrink-0" />
                      <div className="space-y-2">
                        <div className="w-32 h-3 bg-gray-300 rounded" />
                        <div className="w-24 h-3 bg-gray-200 rounded" />
                      </div>
                    </div>
                  </CustomTableData>
                  <CustomTableData dataLabel="Address">
                    <div className="w-40 h-3 bg-gray-300 rounded animate-pulse" />
                  </CustomTableData>

                  <CustomTableData dataLabel="No. of Tenants">
                    <div className="w-10 h-3 bg-gray-300 rounded animate-pulse" />
                  </CustomTableData>
                  <CustomTableData dataLabel="Status" className="px-4 md:px-6 text-sm">
                    <div className="w-16 h-3 bg-gray-300 rounded animate-pulse" />
                  </CustomTableData>

                  <CustomTableDataCTA dataLabel="" text="">
                    <div className="w-6 h-3 bg-gray-300 rounded animate-pulse" />
                  </CustomTableDataCTA>
                </CustomTableRow>
              ))
            ) : visibleProperties.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center py-6 text-gray-500 text-sm">
                  No properties available.
                </td>
              </tr>
            ) : (
              visibleProperties.map((property) => (
                <CustomTableRow
                  key={property.id}
                  className="cursor-pointer"
                >
                  <CustomTableData dataLabel="Landlord" text="" className="px-4 md:px-6">
                    <div className="flex items-center space-x-3">
                      <img
                        src={property.imageUrls?.[0] || profileIcon}
                        alt="Profile"
                        className="w-10 h-10 rounded-full object-cover shrink-0"
                      />
                      <div className="truncate max-w-[180px] sm:max-w-xs">
                        <p className="text-black-700 font-semibold text-sm md:text-base truncate">
                          {property.communityMemberId?.resident?.firstName}{' '}
                          {property.communityMemberId?.resident?.lastName ||
                            'Not available'}
                        </p>
                        <p className="text-gray-400 text-xs md:text-sm truncate">
                          {property.communityMemberId?.resident?.email || 'Not available'}
                        </p>
                      </div>
                    </div>
                  </CustomTableData>

                  <CustomTableData
                    dataLabel="Address"
                    text={property.address}
                    className="px-4 md:px-6 text-sm whitespace-normal min-w-[200px]"
                  />

                  <CustomTableData
                    dataLabel="No. of Tenants"
                    text={property.membersCount}
                    className="px-4 md:px-6 text-sm"
                  />

                  {/* <CustomTableDataCTA dataLabel="" text="...">
                    {['tenant', 'landlord', 'capacity'].map((type) => (
                      <p
                        key={type}
                        onClick={() => handleOpenModal(type as ModalType, property)}
                        className="hover:bg-gray-200 p-2 md:p-3 border-t-2 text-xs md:text-sm"
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)} Info
                      </p>
                    ))}
                  </CustomTableDataCTA> */}
                  <CustomTableData dataLabel="Status" className="px-4 md:px-6 text-sm">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 text-xs font-medium ${
                        isEstateApproved(property.estateApproved, property.ownerApproved)
                          ? 'text-green-700'
                          : ' text-red-700'
                      }`}
                    >
                      {isEstateApproved(property.estateApproved, property.ownerApproved)
                        ? 'Active'
                        : 'Inactive'}
                    </span>
                  </CustomTableData>
                  <CustomTableDataCTA dataLabel="" text="...">
                    <p
                      onClick={() => handleOpenModal('tenant', property)}
                      className="hover:bg-gray-200 p-2 md:p-3 border-t-2 text-xs md:text-sm"
                    >
                      Tenant Info
                    </p>
                    <p
                      onClick={() => handleOpenModal('landlord', property)}
                      className="hover:bg-gray-200 p-2 md:p-3 border-t-2 text-xs md:text-sm"
                    >
                      Landlord Info
                    </p>
                    <p
                      onClick={() => handleOpenModal('capacity', property)}
                      className="hover:bg-gray-200 p-2 md:p-3 border-t-2 text-xs md:text-sm"
                    >
                      Capacity Info
                    </p>
                    {!property.estateApproved && (
                      <p
                        onClick={() => handleOpenModal('approval', property)}
                        className="hover:bg-gray-200 p-2 md:p-3 border-t-2 text-xs md:text-sm text-green-600 font-medium"
                      >
                        Approve
                      </p>
                    )}
                  </CustomTableDataCTA>
                </CustomTableRow>
              ))
            )}
          </CustomTableBody>
        </CustomTable>
      </div>

      {/* Modal */}
      {modalType && selectedProperty && (
        <Modal open={!!modalType} close={handleCloseModal} widthClass="w-fit">
          <div className="pt-5 px-4 md:px-6 relative max-h-[85vh] overflow-y-auto w-full mx-auto">
            <button
              onClick={handleCloseModal}
              className="absolute top-2 right-3 text-gray-600 hover:text-black text-2xl z-10"
            >
              &times;
            </button>

            <h2 className="text-lg md:text-xl font-semibold mb-4 pr-6">
              {modalType === 'tenant' && 'View Tenants'}
              {modalType === 'landlord' && 'Landlord’s Details'}
              {modalType === 'capacity' && 'Property Capacity'}
              {modalType === 'approval' && ' Property Information'}
            </h2>
            {modalType === 'approval' && (
              <>
                <div>
                  {/* Property Information */}
                  <div className="grid grid-cols-1 gap-3 mb-6">
                    <div className="rounded-lg bg-gray-50 px-4 py-3">
                      <p className="text-xs text-gray-500 mb-1">Property Address</p>
                      <p className="text-sm font-semibold text-gray-900 break-words">
                        {selectedProperty.address || 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Landlord Information */}
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    Landlord Information
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                    <div className="rounded-lg bg-gray-50 px-4 py-3">
                      <p className="text-xs text-gray-500 mb-1">Full Name</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedProperty.communityMemberId?.resident?.fullName || 'N/A'}
                      </p>
                    </div>

                    <div className="rounded-lg bg-gray-50 px-4 py-3">
                      <p className="text-xs text-gray-500 mb-1">Phone Number</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedProperty.communityMemberId?.resident?.phone || 'N/A'}
                      </p>
                    </div>

                    <div className="rounded-lg bg-gray-50 px-4 py-3 sm:col-span-2">
                      <p className="text-xs text-gray-500 mb-1">Email Address</p>
                      <p className="text-sm font-semibold text-gray-900 break-words">
                        {selectedProperty.communityMemberId?.resident?.email || 'N/A'}
                      </p>
                    </div>

                    <div className="rounded-lg bg-gray-50 px-4 py-3 sm:col-span-2">
                      <p className="text-xs text-gray-500 mb-1">Landlord Address</p>
                      <p className="text-sm font-semibold text-gray-900 break-words">
                        {selectedProperty.communityMemberId?.resident?.address || 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Agent Information */}
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    Agent Information
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                    <div className="rounded-lg bg-gray-50 px-4 py-3">
                      <p className="text-xs text-gray-500 mb-1">Agent Name</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedProperty.agentName || 'N/A'}
                      </p>
                    </div>

                    <div className="rounded-lg bg-gray-50 px-4 py-3">
                      <p className="text-xs text-gray-500 mb-1">Phone Number</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedProperty.agentPhone || 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Occupancy */}
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Occupancy</h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                    <div className="rounded-lg bg-gray-50 px-4 py-3">
                      <p className="text-xs text-gray-500 mb-1">
                        Number of Members / Occupants
                      </p>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedProperty.membersCount ?? 0}
                      </p>
                    </div>

                    <div className="rounded-lg bg-gray-50 px-4 py-3">
                      <p className="text-xs text-gray-500 mb-1">Property Capacity</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedProperty.capacity?.length
                          ? selectedProperty.capacity.reduce(
                              (total, item) => total + (item.capacity || 0),
                              0
                            )
                          : 'N/A'}
                      </p>
                    </div>
                    <div className="rounded-lg bg-gray-50 px-4 py-3">
                      <p className="text-xs text-gray-500 mb-1">Occupied Capacity</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedProperty.capacity?.length
                          ? selectedProperty.capacity.reduce(
                              (total, item) => total + (item.takenCapacity || 0),
                              0
                            )
                          : 'N/A'}
                      </p>
                    </div>

                    <div className="rounded-lg bg-gray-50 px-4 py-3">
                      <p className="text-xs text-gray-500 mb-1">Available Capacity</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedProperty.capacity?.length
                          ? selectedProperty.capacity.reduce(
                              (total, item) =>
                                total +
                                Math.max(
                                  (item.capacity || 0) - (item.takenCapacity || 0),
                                  0
                                ),
                              0
                            )
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                  {/* Current Property Status */}
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    Current Property Status
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                    {/* Estate Approval */}
                    <div className="rounded-lg bg-gray-50 px-4 py-3 flex items-center justify-between gap-3">
                      <p className="text-xs text-gray-500">Estate Approval</p>

                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          selectedProperty.estateApproved
                            ? 'bg-green-100 text-green-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {selectedProperty.estateApproved ? 'Approved' : 'Pending'}
                      </span>
                    </div>

                    {/* Owner Approval */}
                    <div className="rounded-lg bg-gray-50 px-4 py-3 flex items-center justify-between gap-3">
                      <p className="text-xs text-gray-500">Owner Approval</p>

                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          selectedProperty.ownerApproved
                            ? 'bg-green-100 text-green-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {selectedProperty.ownerApproved ? 'Approved' : 'Pending'}
                      </span>
                    </div>
                  </div>
                  {selectedProperty.estateApproved === false &&
                    selectedProperty.rejectionReason && (
                      <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                        <p className="mb-1 text-xs font-medium text-red-600">
                          Reason for Rejection
                        </p>
                        <p className="text-sm font-semibold text-gray-900 break-words">
                          {selectedProperty.rejectionReason}
                        </p>
                      </div>
                    )}
                  <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 mb-8 border-t border-gray-100">
                    <DefaultButton
                      type="button"
                      text="Reject Property"
                      buttonType="secondary"
                      onClick={handleOpenRejectModal}
                      disabled={rejectLoading}
                      borderColor="red.200"
                      color="red.600"
                      _hover={{
                        background: 'red.50',
                      }}
                    />

                    <DefaultButton
                      type="button"
                      text="Approve Property"
                      onClick={() => handleApproveProperty(selectedProperty)}
                      disabled={approveLoading}
                      loading={approveLoading}
                    />
                  </div>
                </div>
              </>
            )}

            {modalType === 'landlord' && (
              <>
                {landlordProperties.length > 0 ? (
                  <div className="flex flex-col w-full mb-6 py-2 text-sm md:text-base">
                    <p className="w-full mb-3 break-words">
                      Name of Landlord:{' '}
                      <span className="font-semibold block sm:inline">
                        {landlordProperties[0].communityMemberId?.resident?.firstName ??
                          ''}{' '}
                        {landlordProperties[0].communityMemberId?.resident?.lastName ??
                          ''}
                      </span>
                    </p>
                    <p className="w-full mb-3 break-words">
                      Phone number:{' '}
                      <span className="font-semibold block sm:inline">
                        {landlordProperties[0].communityMemberId?.resident?.phone ??
                          'N/A'}
                      </span>
                    </p>
                    <p className="w-full mb-3 break-words">
                      Email Address:{' '}
                      <span className="font-semibold block sm:inline">
                        {landlordProperties[0].communityMemberId?.resident?.email ??
                          'N/A'}
                      </span>
                    </p>
                    <p className="w-full mb-3 break-words">
                      Total number of properties:{' '}
                      <span className="font-semibold">{landlordProperties.length}</span>
                    </p>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4 text-sm">
                    No landlord details available.
                  </p>
                )}
              </>
            )}

            {modalType === 'capacity' && (
              <div className="w-full overflow-x-auto">
                {Array.isArray(selectedProperty.capacity) &&
                selectedProperty.capacity.length > 0 ? (
                  <CustomTable>
                    <CustomTableHead
                      dataList={['Resident Type', 'Max Capacity', 'Taken Capacity']}
                    />
                    <CustomTableBody loading={false}>
                      {selectedProperty.capacity.map((item) => (
                        <CustomTableRow key={item._id}>
                          <CustomTableData
                            dataLabel="Resident Type"
                            text={item.residentType?.title ?? 'N/A'}
                            className="px-2 md:px-6 text-sm"
                          />
                          <CustomTableData
                            dataLabel="Max Capacity"
                            text={item.capacity?.toString() ?? 'N/A'}
                            className="px-2 md:px-6 text-sm"
                          />
                          <CustomTableData
                            dataLabel="Taken Capacity"
                            text={item.takenCapacity?.toString() ?? 'N/A'}
                            className="px-2 md:px-6 text-sm"
                          />
                        </CustomTableRow>
                      ))}
                    </CustomTableBody>
                  </CustomTable>
                ) : (
                  <p className="text-gray-500 text-center py-4 text-sm">
                    No capacity information available.
                  </p>
                )}
              </div>
            )}

            {modalType === 'tenant' && (
              <div className="w-full overflow-x-auto">
                {tenantsLoading ? (
                  <p className="text-gray-500 text-center py-4 text-sm">
                    Loading tenant details...
                  </p>
                ) : tenantsData?.members?.length > 0 ? (
                  <CustomTable>
                    <CustomTableHead
                      dataList={[
                        'Name of Tenant',
                        'Phone Number',
                        'Email Address',
                        'Flat Number',
                      ]}
                    />
                    <CustomTableBody loading={false}>
                      {tenantsData.members.map((member: MemberDTO) => {
                        const tenant = member.resident
                        return (
                          <CustomTableRow key={tenant?.id}>
                            <CustomTableData
                              dataLabel="Name of Tenant"
                              text={`${tenant?.firstName ?? ''} ${tenant?.lastName ?? ''}`}
                              className="px-2 md:px-6 text-sm space-y-10"
                            />
                            <CustomTableData
                              dataLabel="Phone Number"
                              text={tenant?.phone ?? 'N/A'}
                              className="px-2 md:px-6 text-sm"
                            />
                            <CustomTableData
                              dataLabel="Email Address"
                              text={tenant?.email ?? 'N/A'}
                              className="px-2 md:px-6 text-sm"
                            />
                            <CustomTableData
                              dataLabel="Flat Number"
                              text={tenant?.address ?? 'N/A'}
                              className="px-2 md:px-6 text-sm"
                            />
                          </CustomTableRow>
                        )
                      })}
                    </CustomTableBody>
                  </CustomTable>
                ) : (
                  <p className="text-gray-500 text-center py-4 text-sm">
                    No tenant information available.
                  </p>
                )}
              </div>
            )}

            {modalType !== 'approval' && (
              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="border-2 border-gray-300 hover:bg-gray-300 text-gray-800 py-1.5 md:py-2 px-4 md:px-6 rounded-md text-sm"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}
      {showRejectModal && selectedProperty && (
        <Modal
          open={showRejectModal}
          close={() => {
            if (!rejectLoading) {
              setShowRejectModal(false)
              setRejectReason('')
            }
          }}
          widthClass="w-[calc(100%-32px)] sm:max-w-md"
        >
          <div className="p-2">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Reject Property</h2>

            <p className="text-sm text-gray-500 mb-5">
              Please provide a reason for rejecting this property.
            </p>

            <div className="mb-5">
              <label
                htmlFor="rejectReason"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Rejection Reason
              </label>

              <textarea
                id="rejectReason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection"
                rows={4}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm outline-none focus:ring-2 focus:ring-gray-900"
                disabled={rejectLoading}
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                disabled={rejectLoading}
                onClick={() => {
                  setShowRejectModal(false)
                  setRejectReason('')
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={rejectLoading || !rejectReason.trim()}
                onClick={handleRejectProperty}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {rejectLoading ? 'Rejecting...' : 'Reject Property'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Pagination */}
      <div className="w-full flex justify-center sm:justify-end overflow-x-auto">
        <TablePagination
          totalPages={totalPages}
          currPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </div>
    </div>
  )
}

export default ViewProperty
